import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { APP_CONFIG } from "../config/app-config";
import {
  clearPersistedPartnerSlug,
  loginPathForSlug,
  normalizePartnerSlug,
  persistPartnerSlug,
  readPersistedPartnerSlug,
} from "../utils/partnerSlugSession";
import {
  formatLocalWalletDisplay,
  formatPhoneNumber,
  isValidSAMobile,
  phonesMatch,
} from "../utils/registerPhone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Alert,
  AlertDescription,
} from "../components/ui/alert";
import {
  Eye,
  EyeOff,
  Phone,
  Check,
  X,
  AlertTriangle,
  FileText,
  HelpCircle,
  Info,
  CheckCircle,
  CreditCard,
  Gift,
} from "lucide-react";
import {
  evaluatePassword,
  evaluateStrength,
  getPasswordErrors,
  PASSWORD_CONTEXTUAL_MESSAGE,
  PASSWORD_BREACHED_MESSAGE,
} from "../utils/passwordPolicy";
import type { PasswordContext } from "../utils/passwordPolicy";
// Local validators for ID and phone/email
// SA driver's licence and SA temporary ID are NOT accepted for KYC/registration
// (removed 2026-07-30). Only SA ID and passports are accepted going forward.
type IdType = 'south_african_id' | 'passport' | 'generic';

interface IdValidationResult { isValid: boolean; type: IdType; reason?: string }

const luhn13 = (digits: string): boolean => {
  if (!/^\d{13}$/.test(digits)) return false;
  let sum = 0; let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = parseInt(digits[i], 10);
    if (alt) { d *= 2; if (d > 9) d -= 9; }
    sum += d; alt = !alt;
  }
  return sum % 10 === 0;
};

const validateIdNumber = (value: string): IdValidationResult => {
  const v = (value || '').replace(/\s/g, '');
  if (!v) return { isValid: false, type: 'generic', reason: 'Required' };
  // SA ID
  if (/^\d{13}$/.test(v)) {
    return luhn13(v)
      ? { isValid: true, type: 'south_african_id' }
      : { isValid: false, type: 'south_african_id', reason: 'Checksum failed' };
  }
  // Passport: 6–9 alphanumeric
  if (/^[A-Z0-9]{6,9}$/i.test(v)) {
    return { isValid: true, type: 'passport' };
  }
  return { isValid: false, type: 'generic', reason: 'Invalid format' };
};

const getIdPlaceholderText = (t: IdType) => {
  switch (t) {
    case 'south_african_id':
      return '13 digits (e.g., 6411055084084)';
    case 'passport':
      return '6-9 alphanumeric (e.g., A05529179)';
    default:
      return '13-digit SA ID or 6-9 char passport';
  }
};

const getIdHelperText = (res: IdValidationResult) => {
  if (!res) return '';
  if (!res.isValid && res.reason === 'Required') return 'Required';
  if (res.type === 'south_african_id') return res.isValid ? 'SOUTH AFRICAN ID - Valid' : 'SA ID must be 13 digits and pass checksum';
  if (res.type === 'passport') return res.isValid ? 'Passport - Valid' : 'Passport must be 6–9 alphanumeric characters';
  return res.isValid ? 'Generic ID - Valid' : 'Invalid ID format';
};

import {
  validateEmail as validateEmailFormat,
  isValidEmail,
  getEmailTypoSuggestion,
} from "../utils/emailValidation";

// Import logo from assets/
import logo2 from "../assets/logo2.svg";
import { SecurityBadge } from "../components/SecurityBadge";
import { Checkbox } from "../components/ui/checkbox";
import {
  WALLET_REGISTRATION_DISCLOSURE,
  WALLET_TERMS_VERSION,
} from "../constants/walletRegistrationTerms";

// Multi-input detection utilities (same as authentication)
const detectInputType = (
  input: string,
): "phone" | "account" | "username" | "unknown" => {
  const cleanInput = input.trim();

  // Phone number patterns (SA format)
  const phonePattern = /^(\+27|27|0)?[6-8][0-9]{8}$/;
  if (phonePattern.test(cleanInput.replace(/\s/g, ""))) {
    return "phone";
  }

  // Account number pattern (8-12 digits only)
  const accountPattern = /^[0-9]{8,12}$/;
  if (accountPattern.test(cleanInput)) {
    return "account";
  }

  // Username pattern (4-32 chars, letters/numbers/periods/underscores)
  const usernamePattern = /^[a-zA-Z0-9._]{4,32}$/;
  if (usernamePattern.test(cleanInput)) {
    return "username";
  }

  return "unknown";
};

// Password validation — delegates to the shared password policy
// (single source of truth, mirrors the backend at utils/passwordPolicy.js).
// Required: 8+ chars, a letter, a number. Uppercase/special are optional.
// Also rejects passwords derived from the user's own details (name/phone/ID/
// email) and common weak passwords, matching the backend enforcement.
const validatePassword = (password: string, context?: PasswordContext) => {
  const { isValid, checks } = evaluatePassword(password);
  const strength = evaluateStrength(password, context);
  return { ...checks, isValid: isValid && strength.ok, strengthErrors: strength.errors };
};

// Identifier validation
const validateIdentifier = (
  identifier: string,
  type: string,
): { isValid: boolean; message?: string } => {
  if (!identifier.trim()) {
    return {
      isValid: false,
      message: "This field is required",
    };
  }

  switch (type) {
    case "phone": {
      const phonePattern = /^(\+27|27|0)[6-8][0-9]{8}$/;
      if (!phonePattern.test(identifier.replace(/\s/g, ""))) {
        return {
          isValid: false,
          message: "Invalid South African mobile number",
        };
      }
      return { isValid: true };
    }

    case "account":
      if (!/^[0-9]{8,12}$/.test(identifier)) {
        return {
          isValid: false,
          message: "Account number must be 8-12 digits",
        };
      }
      return { isValid: true };

    case "username":
      if (!/^[a-zA-Z0-9._]{4,32}$/.test(identifier)) {
        return {
          isValid: false,
          message:
            "Username must be 4-32 characters (letters, numbers, periods, underscores)",
        };
      }
      return { isValid: true };

    default:
      return {
        isValid: false,
        message:
          "Please enter a valid phone number, account number, or username",
      };
  }
};

interface PartnerBranding {
  slug: string;
  companyName: string;
  displayName: string;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug: rawParamSlug } = useParams();
  const rawQuerySlug = searchParams.get('partner') || searchParams.get('partnerSlug') || searchParams.get('slug') || '';
  const urlPartnerSlug = normalizePartnerSlug(rawParamSlug || rawQuerySlug);
  const partnerSlug = urlPartnerSlug || readPersistedPartnerSlug();
  const { register, isLoading } = useAuth();

  // Remember the invite slug for this tab so Sign In → Create Wallet cannot
  // drop partner attribution (the hop previously navigated to bare /register).
  useEffect(() => {
    if (urlPartnerSlug) persistPartnerSlug(urlPartnerSlug);
  }, [urlPartnerSlug]);

  // Partner-scoped self-registration link (e.g. /register/<white_label_slug> or /register?partner=<slug>).
  // Resolve the slug to public branding so the rep sees who they are joining,
  // and so we attribute their wallet to that partner at registration.
  const [partner, setPartner] = useState<PartnerBranding | null>(null);
  useEffect(() => {
    if (!partnerSlug) {
      setPartner(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${APP_CONFIG.API.baseUrl}/api/v1/disbursement-clients/public/by-slug/${encodeURIComponent(partnerSlug)}`,
        );
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && json?.success && json?.data) {
          setPartner({
            slug: json.data.slug,
            companyName: json.data.companyName,
            displayName: json.data.branding?.displayName || json.data.companyName,
          });
        }
      } catch {
        // Non-blocking: an unresolved slug just renders the standard page.
      }
    })();
    return () => { cancelled = true; };
  }, [partnerSlug]);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    surname: "",
    identifier: "",
    confirmIdentifier: "",
    idNumber: "",
    idType: 'generic' as IdType,
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });

  // Prefill referral code from URL (?ref= or ?referralCode=)
  useEffect(() => {
    const ref = searchParams.get('ref') || searchParams.get('referralCode') || '';
    if (ref) {
      setFormData((prev) => ({ ...prev, referralCode: ref.trim().toUpperCase() }));
    }
  }, [searchParams]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPasswordValidation, setShowPasswordValidation] =
    useState(false);
  const [showPasswordHelp, setShowPasswordHelp] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const errorAlertRef = useRef<HTMLDivElement>(null);
  const termsSectionRef = useRef<HTMLDivElement>(null);

  // Real-time validation
  const inputType = detectInputType(formData.identifier);
  const identifierValidation = { isValid: isValidSAMobile(formData.identifier), message: isValidSAMobile(formData.identifier) ? undefined : 'Invalid South African mobile number' };
  const mobilesMatch = phonesMatch(formData.identifier, formData.confirmIdentifier);
  const walletNumberPreview = formatLocalWalletDisplay(formData.identifier);
  const idValidation = validateIdNumber(formData.idNumber);
  // Context for the password contextual check (name/phone/ID/email must not
  // appear in the password). The mobile number is captured in `identifier`.
  const passwordContext: PasswordContext = {
    firstName: formData.firstName,
    lastName: formData.surname,
    phoneNumber: formData.identifier,
    idNumber: formData.idNumber,
    email: formData.email,
  };
  const passwordValidation = validatePassword(
    formData.password,
    passwordContext,
  );
  const passwordsMatch =
    formData.password === formData.confirmPassword;
  const emailCheck = validateEmailFormat(formData.email);
  const emailValid = emailCheck.isValid;

  const getRegistrationBlockers = (): string[] => {
    const blockers: string[] = [];
    if (!formData.firstName.trim()) {
      blockers.push("Please enter your first name.");
    }
    if (!formData.surname.trim()) {
      blockers.push("Please enter your surname.");
    }
    if (!identifierValidation.isValid) {
      blockers.push("Please enter a valid South African mobile number.");
    }
    if (!formData.confirmIdentifier.trim()) {
      blockers.push("Please confirm your mobile number.");
    } else if (!mobilesMatch) {
      blockers.push("Mobile numbers do not match.");
    }
    if (!idValidation.isValid) {
      blockers.push(
        !formData.idNumber.trim()
          ? "Please enter your ID number or passport."
          : getIdHelperText(idValidation),
      );
    }
    if (!formData.email.trim() || !emailValid) {
      blockers.push("Please enter a valid email address.");
    }
    if (!formData.password) {
      blockers.push("Please create a password.");
    } else if (!passwordValidation.isValid) {
      blockers.push("Please fix your password using the requirements below.");
    } else if (!passwordsMatch) {
      blockers.push("Passwords do not match — please confirm your password.");
    }
    if (!acceptedTerms) {
      blockers.push(
        "Please scroll down and accept the Important Wallet Notice and Terms & Conditions.",
      );
    }
    return blockers;
  };

  const registrationBlockers = getRegistrationBlockers();
  const isSubmitBlocked = registrationBlockers.length > 0 || isLoading;

  const showRegistrationBlockers = () => {
    if (isLoading || registrationBlockers.length === 0) {
      return;
    }

    if (
      formData.password.length > 0 &&
      !passwordValidation.isValid
    ) {
      setShowPasswordHelp(true);
      return;
    }

    setError(registrationBlockers[0]);

    const termsOnlyRemaining =
      !acceptedTerms &&
      registrationBlockers.length === 1 &&
      registrationBlockers[0].includes("Terms & Conditions");

    if (termsOnlyRemaining) {
      termsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else {
      errorAlertRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const getPlaceholderText = () => {
    switch (inputType) {
      case "phone":
        return "0XX XXX XXXX";
      case "account":
        return "12345678";
      case "username":
        return "username";
      default:
        return "0XX XXX XXXX";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (registrationBlockers.length > 0) {
      showRegistrationBlockers();
      return;
    }

    setError("");

    try {
      await register!({
        firstName: formData.firstName.trim(),
        surname: formData.surname.trim(),
        name: `${formData.firstName} ${formData.surname}`.trim(),
        identifier: formData.identifier,
        confirmPhoneNumber: formData.confirmIdentifier,
        idNumber: formData.idNumber,
        idType: idValidation.type as any,
        email: formData.email,
        password: formData.password,
        identifierType: inputType as
          | "phone"
          | "account"
          | "username",
        referralCode: formData.referralCode?.trim() || undefined,
        partnerSlug: partner?.slug || partnerSlug || readPersistedPartnerSlug() || undefined,
      });
      clearPersistedPartnerSlug();
      navigate("/dashboard");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Registration failed. Please try again.",
      );
      // The error banner sits at the top of the page; after submitting from the
      // bottom the user would otherwise not see it (it looked like "nothing
      // happened" / a thin red line). Bring it into view.
      requestAnimationFrame(() => {
        errorAlertRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#86BE41] to-[#2D8CCA]">
      <div className="mobile-container">
        <div
          style={{
            padding: "var(--mobile-padding)",
            paddingTop: "2rem",
            paddingBottom: "2rem",
          }}
        >
          {/* Header with Logo - IDENTICAL SIZING TO LOGIN PAGE */}
          <div className="text-center mb-8">
            {/* Logo2.svg - Consistent sizing with proper import */}
            <div className="flex justify-center mb-2">
              <img
                src={logo2}
                alt="MyMoolah Logo"
                className="h-16 w-auto"
                style={{
                  height: "8rem", // Doubled from 4rem to 8rem
                  width: "auto",
                  maxWidth: "400px", // Doubled max width to accommodate larger logo
                  objectFit: "contain",
                }}
              />
            </div>

            <h1
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "clamp(1.5rem, 4vw, 2rem)",
                fontWeight: "var(--font-weight-bold)",
                color: "white",
                marginBottom: "0.5rem",
              }}
            >
              Join MyMoolah
            </h1>
            <p
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "var(--mobile-font-base)",
                fontWeight: "var(--font-weight-normal)",
                color: "rgba(255, 255, 255, 0.9)",
              }}
            >
              Create your digital wallet account
            </p>
          </div>

          {/* Partner-scoped registration banner (e.g. Liqui Moly rep link) */}
          {partner && (
            <div
              className="mb-4 rounded-lg bg-white/90 backdrop-blur-sm border border-white/60 p-3 text-center"
              role="status"
            >
              <p
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "var(--mobile-font-small)",
                  color: "#6b7280",
                }}
              >
                You're registering as a representative of
              </p>
              <p
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "var(--mobile-font-base)",
                  fontWeight: "var(--font-weight-bold)",
                  color: "#1f2937",
                }}
              >
                {partner.displayName}
              </p>
            </div>
          )}

          {/* Main Register Card */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl" style={{ marginTop: "-4rem" }}>
            <CardHeader style={{ paddingBottom: "1rem" }}>
              <CardTitle
                className="text-center"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "clamp(1.125rem, 2.5vw, 1.25rem)",
                  fontWeight: "var(--font-weight-bold)",
                  color: "#1f2937",
                }}
              >
                Create Wallet
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert ref={errorAlertRef} className="border-red-200 bg-red-50 mb-4">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      color: "#dc2626",
                    }}
                  >
                    {error}
                  </AlertDescription>
                </Alert>
              )}
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                {/* First Name */}
                <div className="space-y-2">
                  <Label
                    htmlFor="firstName"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41]"
                    style={{
                      height: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-normal)",
                      borderRadius:
                        "var(--mobile-border-radius)",
                    }}
                    required
                  />
                </div>

                {/* Surname */}
                <div className="space-y-2">
                  <Label
                    htmlFor="surname"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Surname
                  </Label>
                  <Input
                    id="surname"
                    type="text"
                    placeholder="Enter your surname"
                    value={formData.surname}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        surname: e.target.value,
                      }))
                    }
                    className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41]"
                    style={{
                      height: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-normal)",
                      borderRadius:
                        "var(--mobile-border-radius)",
                    }}
                    required
                  />
                </div>

                {/* Multi-Input Identifier Field */}
                <div className="space-y-2">
                  <Label
                    htmlFor="identifier"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Phone Number
                  </Label>
                  <div className="relative">
                    <Input
                      id="identifier"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder={getPlaceholderText()}
                      value={formData.identifier}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          identifier: formatPhoneNumber(e.target.value),
                        }))
                      }
                      className={`bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12 ${
                        !identifierValidation.isValid &&
                        formData.identifier.trim()
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : identifierValidation.isValid &&
                              formData.identifier.trim()
                            ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                            : ""
                      }`}
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        fontWeight: "var(--font-weight-normal)",
                        borderRadius:
                          "var(--mobile-border-radius)",
                      }}
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Phone className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  <div
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-small)",
                    }}
                  >
                    {formData.identifier.trim() ? (
                      <span
                        className={`inline-flex items-center gap-1 ${identifierValidation.isValid ? "text-green-600" : "text-red-600"}`}
                      >
                        {identifierValidation.isValid ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        {identifierValidation.isValid
                          ? `Your wallet number will be ${walletNumberPreview}`
                          : "Enter SA mobile e.g. 078 456 0585"}
                      </span>
                    ) : (
                      <span style={{ color: "#6b7280" }}>
                        Enter your mobile number (0XX XXX XXXX) — this is also your login / account number.
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="confirmIdentifier"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Confirm mobile number
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmIdentifier"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="Re-type your mobile number"
                      value={formData.confirmIdentifier}
                      onPaste={(e) => e.preventDefault()}
                      onDrop={(e) => e.preventDefault()}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          confirmIdentifier: formatPhoneNumber(e.target.value),
                        }))
                      }
                      className={`bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12 ${
                        formData.confirmIdentifier.trim() && !mobilesMatch
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : mobilesMatch
                            ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                            : ""
                      }`}
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        fontWeight: "var(--font-weight-normal)",
                        borderRadius:
                          "var(--mobile-border-radius)",
                      }}
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Phone className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-small)",
                    }}
                  >
                    {formData.confirmIdentifier.trim() ? (
                      <span
                        className={`inline-flex items-center gap-1 ${mobilesMatch ? "text-green-600" : "text-red-600"}`}
                      >
                        {mobilesMatch ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        {mobilesMatch
                          ? "Mobile numbers match"
                          : "Mobile numbers do not match"}
                      </span>
                    ) : (
                      <span style={{ color: "#6b7280" }}>
                        Type the number again. Do not paste.
                      </span>
                    )}
                  </div>
                </div>

                {/* ID Number / Passport */}
                <div className="space-y-2">
                  <Label
                    htmlFor="idNumber"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    ID Number / Passport Number
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-2" style={{
                    fontFamily: "Montserrat, sans-serif",
                  }}>
                    Enter your 13-digit South African ID number or international passport number (6-9 alphanumeric characters)
                  </p>
                  <div className="relative">
                    <Input
                      id="idNumber"
                      type="text"
                      placeholder={getIdPlaceholderText(idValidation.type)}
                      value={formData.idNumber}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          idNumber: e.target.value,
                        }))
                      }
                      className={`bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12 ${
                        !idValidation.isValid &&
                        formData.idNumber.trim()
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : idValidation.isValid &&
                              formData.idNumber.trim()
                            ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                            : ""
                      }`}
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        fontWeight: "var(--font-weight-normal)",
                        borderRadius:
                          "var(--mobile-border-radius)",
                      }}
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  <div
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-small)",
                    }}
                  >
                    {formData.idNumber.trim() ? (
                      <span
                        className={`inline-flex items-center gap-1 ${idValidation.isValid ? "text-green-600" : "text-red-600"}`}
                      >
                        {idValidation.isValid ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        {getIdHelperText(idValidation)}
                      </span>
                    ) : (
                      <span style={{ color: "#6b7280" }}>
                        Enter your ID number or passport for verification
                      </span>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className={`bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] ${
                      formData.email && !emailValid ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :
                      formData.email && emailValid ? 'border-green-300 focus:border-green-500 focus:ring-green-500' : ''
                    }`}
                    style={{
                      height: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-normal)",
                      borderRadius:
                        "var(--mobile-border-radius)",
                    }}
                    required
                  />
                  <div
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 'var(--mobile-font-small)'
                    }}
                  >
                    {formData.email && (
                      <div className="space-y-1">
                        <span className={`inline-flex items-center gap-1 ${emailValid ? 'text-green-600' : 'text-red-600'}`}>
                          {emailValid ? 'Valid email' : (emailCheck.error || 'Invalid email format (e.g., user@example.com)')}
                        </span>
                        {emailCheck.suggestion && (
                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  email: emailCheck.suggestion!,
                                }))
                              }
                              className="text-xs text-[#002D62] hover:underline font-medium cursor-pointer"
                            >
                              Did you mean: <span className="font-semibold">{emailCheck.suggestion}</span>? Click to fix
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Password Field - WITH BLUE FORMAT HINTS */}
                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Create Password
                  </Label>

                  {/* COMPACT Password Format Hint - ONLY on RegisterPage */}
                  {!passwordFocused &&
                    formData.password.length === 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Info className="w-3 h-3 text-blue-600 flex-shrink-0" />
                          <div className="flex-1">
                            <p
                              className="text-blue-700 text-xs mb-1"
                              style={{
                                fontFamily:
                                  "Montserrat, sans-serif",
                                fontSize: "11px",
                                fontWeight:
                                  "var(--font-weight-bold)",
                              }}
                            >
                              <strong>Format:</strong> 8+ chars,
                              a letter and a number
                            </p>
                            <p
                              className="text-blue-600 text-xs"
                              style={{
                                fontFamily:
                                  "Montserrat, sans-serif",
                                fontSize: "10px",
                                fontWeight:
                                  "var(--font-weight-normal)",
                              }}
                            >
                              <strong>e.g.</strong>{" "}
                              mymoolah2024
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }));
                        setShowPasswordValidation(
                          e.target.value.length > 0,
                        );
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pr-12"
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        fontWeight: "var(--font-weight-normal)",
                        borderRadius:
                          "var(--mobile-border-radius)",
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword(!showPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      style={{
                        minHeight: "var(--mobile-touch-target)",
                        minWidth: "var(--mobile-touch-target)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-label={
                        showPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Real-time Password Validation - ONLY on RegisterPage */}
                  {showPasswordValidation &&
                    formData.password.length > 0 && (
                      <div
                        className="mt-2 p-3 bg-gray-50 rounded-lg border"
                        style={{
                          borderRadius:
                            "var(--mobile-border-radius)",
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p
                            style={{
                              fontFamily:
                                "Montserrat, sans-serif",
                              fontSize:
                                "var(--mobile-font-small)",
                              fontWeight:
                                "var(--font-weight-medium)",
                              color: "#374151",
                            }}
                          >
                            Password Requirements:
                          </p>
                          {passwordValidation.isValid && (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span
                                style={{
                                  fontFamily:
                                    "Montserrat, sans-serif",
                                  fontSize:
                                    "var(--mobile-font-small)",
                                  color: "#16a34a",
                                }}
                              >
                                Valid!
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Required rules — these drive validity */}
                        <div className="grid grid-cols-2 gap-2">
                          <div
                            className={`flex items-center gap-1 text-xs ${passwordValidation.minLength ? "text-green-600" : "text-red-600"}`}
                          >
                            {passwordValidation.minLength ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            <span
                              style={{
                                fontFamily:
                                  "Montserrat, sans-serif",
                                fontSize: "11px",
                              }}
                            >
                              8+ chars
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1 text-xs ${passwordValidation.hasLetter ? "text-green-600" : "text-red-600"}`}
                          >
                            {passwordValidation.hasLetter ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            <span
                              style={{
                                fontFamily:
                                  "Montserrat, sans-serif",
                                fontSize: "11px",
                              }}
                            >
                              A letter
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1 text-xs ${passwordValidation.hasNumber ? "text-green-600" : "text-red-600"}`}
                          >
                            {passwordValidation.hasNumber ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            <span
                              style={{
                                fontFamily:
                                  "Montserrat, sans-serif",
                                fontSize: "11px",
                              }}
                            >
                              A number
                            </span>
                          </div>
                        </div>

                        {/* Optional — encouraged, but never blocks the user */}
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p
                            style={{
                              fontFamily: "Montserrat, sans-serif",
                              fontSize: "10px",
                              color: "#6b7280",
                              marginBottom: "4px",
                            }}
                          >
                            Optional — makes it stronger
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div
                              className={`flex items-center gap-1 text-xs ${passwordValidation.hasUppercase ? "text-green-600" : "text-gray-400"}`}
                            >
                              <Check className="w-3 h-3" />
                              <span
                                style={{
                                  fontFamily:
                                    "Montserrat, sans-serif",
                                  fontSize: "11px",
                                }}
                              >
                                Uppercase (A-Z)
                              </span>
                            </div>
                            <div
                              className={`flex items-center gap-1 text-xs ${passwordValidation.hasSpecialChar ? "text-green-600" : "text-gray-400"}`}
                            >
                              <Check className="w-3 h-3" />
                              <span
                                style={{
                                  fontFamily:
                                    "Montserrat, sans-serif",
                                  fontSize: "11px",
                                }}
                              >
                                Special (!@#$)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                      className={`bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pr-12 ${
                        formData.confirmPassword &&
                        !passwordsMatch
                          ? "border-red-300"
                          : ""
                      }`}
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        fontWeight: "var(--font-weight-normal)",
                        borderRadius:
                          "var(--mobile-border-radius)",
                      }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(
                          !showConfirmPassword,
                        )
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      style={{
                        minHeight: "var(--mobile-touch-target)",
                        minWidth: "var(--mobile-touch-target)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-label={
                        showConfirmPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {formData.confirmPassword && (
                    <div
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-small)",
                      }}
                    >
                      <span
                        className={`inline-flex items-center gap-1 ${passwordsMatch ? "text-green-600" : "text-red-600"}`}
                      >
                        {passwordsMatch ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        {passwordsMatch
                          ? "Passwords match"
                          : "Passwords do not match"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Referral Code (optional) */}
                <div className="space-y-2">
                  <Label
                    htmlFor="referralCode"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    Referral Code <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="referralCode"
                      type="text"
                      placeholder="e.g. REF-F31FC6"
                      value={formData.referralCode}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          referralCode: e.target.value.trim().toUpperCase(),
                        }))
                      }
                      className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12"
                      style={{
                        height: "var(--mobile-touch-target)",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-base)",
                        fontWeight: "var(--font-weight-normal)",
                        borderRadius: "var(--mobile-border-radius)",
                        letterSpacing: "0.5px",
                      }}
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Gift className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "var(--mobile-font-small)",
                    color: "#6b7280",
                  }}>
                    Got a code from a friend? Enter it to earn rewards together.
                  </p>
                </div>

                {/* Section 29 — Short Customer Disclosure (Terms v2.5.0) */}
                <div
                  ref={termsSectionRef}
                  className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3"
                  role="region"
                  aria-labelledby="wallet-registration-disclosure-title"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle
                      className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <div className="space-y-2">
                      <p
                        id="wallet-registration-disclosure-title"
                        style={{
                          fontFamily: "Montserrat, sans-serif",
                          fontSize: "var(--mobile-font-base)",
                          fontWeight: "var(--font-weight-bold)",
                          color: "#92400e",
                        }}
                      >
                        {WALLET_REGISTRATION_DISCLOSURE.title}
                      </p>
                      {WALLET_REGISTRATION_DISCLOSURE.paragraphs.map((paragraph) => (
                        <p
                          key={paragraph}
                          style={{
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "var(--mobile-font-small)",
                            lineHeight: "1.5",
                            color: "#78350f",
                          }}
                        >
                          {paragraph}
                        </p>
                      ))}
                      <p
                        style={{
                          fontFamily: "Montserrat, sans-serif",
                          fontSize: "var(--mobile-font-small)",
                          fontWeight: "var(--font-weight-medium)",
                          color: "#92400e",
                        }}
                      >
                        {WALLET_REGISTRATION_DISCLOSURE.acceptanceLine}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 pt-1">
                    <Checkbox
                      id="acceptTerms"
                      checked={acceptedTerms}
                      onCheckedChange={(checked) =>
                        setAcceptedTerms(checked === true)
                      }
                      aria-describedby="accept-terms-description meta-advertising-notice"
                      className="mt-0.5 border-amber-400 data-[state=checked]:bg-[#86BE41] data-[state=checked]:border-[#86BE41]"
                    />
                    <Label
                      htmlFor="acceptTerms"
                      id="accept-terms-description"
                      className="cursor-pointer leading-snug"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-small)",
                        fontWeight: "var(--font-weight-normal)",
                        color: "#374151",
                      }}
                    >
                      I have read the Important Wallet Notice and accept the MyMoolah
                      Terms & Conditions (version {WALLET_TERMS_VERSION}), including
                      the Own Funds and Third-Party Disbursement Value rules.
                    </Label>
                  </div>
                  <p
                    id="meta-advertising-notice"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-small)",
                      fontWeight: "var(--font-weight-normal)",
                      color: "#4b5563",
                      marginTop: "0.75rem",
                      lineHeight: 1.45,
                    }}
                  >
                    When you sign up through one of our adverts, we share limited, hashed sign-up information with Meta to measure our advertising. You can opt out at any time. See our{" "}
                    <a
                      href="https://mymoolah.africa/privacy-policy/"
                      className="text-[#2D8CCA] underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>

                {/* Submit Button — wrapper captures taps when disabled */}
                <div
                  role="presentation"
                  onClick={() => {
                    if (isSubmitBlocked && !isLoading) {
                      showRegistrationBlockers();
                    }
                  }}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitBlocked}
                    aria-disabled={isSubmitBlocked}
                    className={`w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white disabled:opacity-60 ${
                      isSubmitBlocked && !isLoading ? "pointer-events-none" : ""
                    }`}
                    style={{
                      height: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      borderRadius: "var(--mobile-border-radius)",
                    }}
                  >
                    {isLoading ? (
                      <span>Creating Wallet...</span>
                    ) : (
                      <span>Create Wallet</span>
                    )}
                  </Button>
                </div>
              </form>

              {/* Login Link */}
              <div className="mt-6 text-center">
                <p
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "var(--mobile-font-base)",
                    color: "#6b7280",
                  }}
                >
                  Already have an account?{" "}
                  <button
                    onClick={() => {
                      if (partnerSlug) persistPartnerSlug(partnerSlug);
                      navigate(loginPathForSlug(partnerSlug));
                    }}
                    className="text-[#2D8CCA] hover:text-[#2680B8] font-medium underline"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Card with Icons */}
          <div
            className="mt-4 bg-white/20 backdrop-blur-sm"
            style={{
              padding: "var(--mobile-padding)",
              borderRadius: "var(--mobile-border-radius)",
            }}
          >
            <div className="flex items-center justify-between px-2">
              {/* T&C's Icon */}
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="flex flex-col items-center space-y-1 hover:bg-white/20 p-2 rounded-lg transition-all"
                    style={{
                      minHeight: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    <div className="bg-white/30 backdrop-blur-sm rounded-xl w-12 h-12 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <span
                      className="text-white/80"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-small)",
                      }}
                    >
                      T&C's
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent className="mobile-container" aria-describedby="terms-conditions-description">
                  <DialogHeader>
                    <DialogTitle
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize:
                          "clamp(1rem, 2.5vw, 1.125rem)",
                        fontWeight: "var(--font-weight-bold)",
                      }}
                    >
                      Terms & Conditions
                    </DialogTitle>
                    <div id="terms-conditions-description" className="sr-only">
                      MyMoolah Terms and Conditions version {WALLET_TERMS_VERSION},
                      including the Important Wallet Notice for Own Funds and
                      Third-Party Disbursement Value
                    </div>
                  </DialogHeader>
                  <div
                    className="space-y-3 max-h-[60vh] overflow-y-auto pr-1"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-small)",
                      lineHeight: "1.6",
                      color: "#374151",
                    }}
                  >
                    <p
                      style={{
                        fontWeight: "var(--font-weight-bold)",
                        color: "#111827",
                      }}
                    >
                      {WALLET_REGISTRATION_DISCLOSURE.title}
                    </p>
                    {WALLET_REGISTRATION_DISCLOSURE.paragraphs.map((paragraph) => (
                      <p key={`dialog-${paragraph}`}>{paragraph}</p>
                    ))}
                    <p>{WALLET_REGISTRATION_DISCLOSURE.acceptanceLine}</p>
                    <p className="text-gray-500 pt-2 border-t border-gray-100">
                      Full Terms & Conditions version {WALLET_TERMS_VERSION} (effective
                      25 May 2026) govern wallet, disbursement, cash withdrawal and
                      related Platform services. Contact{" "}
                      <a
                        href="mailto:legal@mymoolah.africa"
                        className="text-[#2D8CCA] underline"
                      >
                        legal@mymoolah.africa
                      </a>{" "}
                      for the complete document.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Security Badge */}
              <div className="flex flex-col items-center space-y-1 transform scale-110">
                <SecurityBadge size="sm" clickable={true} compact={false} />
              </div>

              {/* FAQ Icon */}
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="flex flex-col items-center space-y-1 hover:bg-white/20 p-2 rounded-lg transition-all"
                    style={{
                      minHeight: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    <div className="bg-white/30 backdrop-blur-sm rounded-xl w-12 h-12 flex items-center justify-center">
                      <HelpCircle className="w-6 h-6 text-white" />
                    </div>
                    <span
                      className="text-white/80"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "var(--mobile-font-small)",
                      }}
                    >
                      FAQ
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent className="mobile-container" aria-describedby="faq-description">
                  <DialogHeader>
                    <DialogTitle
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize:
                          "clamp(1rem, 2.5vw, 1.125rem)",
                        fontWeight: "var(--font-weight-bold)",
                      }}
                    >
                      Frequently Asked Questions
                    </DialogTitle>
                    <div id="faq-description" className="sr-only">
                      Frequently asked questions about MyMoolah registration and verification process
                    </div>
                  </DialogHeader>
                  <div
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      lineHeight: "1.6",
                    }}
                  >
                    <p>
                      <strong>
                        Q: What documents do I need for
                        verification?
                      </strong>
                    </p>
                    <p>
                      A: South African ID or Passport, plus
                      proof of address (utility bill, bank
                      statement).
                    </p>
                    <br />
                    <p>
                      <strong>
                        Q: How long does account verification
                        take?
                      </strong>
                    </p>
                    <p>
                      A: Usually 2-5 minutes with our automated
                      KYC system.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Password help / explainer modal — opens on a failed submit.
          Explains exactly what is wrong and how to fix it. */}
      <Dialog
        open={showPasswordHelp}
        onOpenChange={setShowPasswordHelp}
      >
        <DialogContent
          className="mobile-container"
          aria-describedby="password-help-description"
        >
          <DialogHeader>
            <DialogTitle
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "clamp(1rem, 2.5vw, 1.125rem)",
                fontWeight: "var(--font-weight-bold)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Let's fix your password
            </DialogTitle>
            <div
              id="password-help-description"
              className="sr-only"
            >
              Guidance on the MyMoolah password requirements and
              how to correct your password
            </div>
          </DialogHeader>

          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "var(--mobile-font-base)",
              lineHeight: "1.6",
            }}
          >
            <p style={{ color: "#374151", marginBottom: "12px" }}>
              Your password just needs a few small changes:
            </p>

            {/* What's wrong + how to fix */}
            <ul
              className="space-y-2"
              style={{ listStyle: "none", padding: 0, margin: 0 }}
            >
              {(() => {
                // Granular base-rule messages (length / letter / number)...
                const baseErrors = getPasswordErrors(formData.password);
                // ...plus contextual (personal-details) and common-password
                // messages from the shared strength check. Drop the generic
                // base message when granular ones already cover it.
                const extraErrors = passwordValidation.strengthErrors.filter(
                  (m) =>
                    m === PASSWORD_CONTEXTUAL_MESSAGE ||
                    m === PASSWORD_BREACHED_MESSAGE,
                );
                return [...baseErrors, ...extraErrors].map((msg) => (
                  <li
                    key={msg}
                    className="flex items-start gap-2"
                    style={{ color: "#dc2626", fontSize: "13px" }}
                  >
                    <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{msg}</span>
                  </li>
                ));
              })()}
            </ul>

            {/* The full rule, in plain language */}
            <div
              className="mt-4 p-3 bg-gray-50 rounded-lg border"
              style={{ borderRadius: "var(--mobile-border-radius)" }}
            >
              <p
                style={{
                  color: "#374151",
                  fontSize: "13px",
                  fontWeight: "var(--font-weight-medium)",
                  marginBottom: "6px",
                }}
              >
                A good password needs:
              </p>
              <ul
                className="space-y-1"
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  fontSize: "13px",
                }}
              >
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-green-600" />
                  At least 8 characters
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-green-600" />
                  At least one letter (a-z or A-Z)
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-green-600" />
                  At least one number (0-9)
                </li>
              </ul>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "12px",
                  marginTop: "8px",
                }}
              >
                Capital letters and symbols (like ! @ # $) are
                welcome and make it stronger, but they are not
                required.
              </p>
              <p
                style={{
                  color: "#374151",
                  fontSize: "13px",
                  marginTop: "8px",
                }}
              >
                <strong>Example:</strong> mymoolah2024
              </p>
            </div>

            {/* Reveal what they typed, so they can see the issue */}
            {formData.password.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="mt-3 flex items-center gap-2 text-sm text-[#2D8CCA] hover:underline"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                {showPassword
                  ? "Hide my password"
                  : "Show my password"}
              </button>
            )}

            <Button
              type="button"
              onClick={() => setShowPasswordHelp(false)}
              className="w-full mt-4 bg-[#86BE41] hover:bg-[#75a838] text-white"
              style={{
                height: "var(--mobile-touch-target)",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: "var(--font-weight-medium)",
                borderRadius: "var(--mobile-border-radius)",
              }}
            >
              Got it, let me fix it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}